import { Box } from 'rizzui/box';
import { Trash, CirclePlus } from 'lucide-react';
import AppButton from '../../../../components/AppButton/AppButton';

function SalonServiceSteps({
    steps,
    isLoading,
    addStep,
    removeStep,
    updateStep,
    stepError = ''
}) {
    // Function to handle wheel event on number inputs
    const handleWheel = (e) => {
        e.target.blur();
    };

    // Function to check if a specific field has an error
    const hasFieldError = (stepId, field) => {
        if (!stepError) return false;

        // Find the step number for this stepId
        const step = steps.find(s => s.id === stepId);
        if (!step) return false;

        const stepNumber = step.step;

        // Check if the error message contains this step and field
        if (field === 'stepName') {
            return stepError.includes(`Step ${stepNumber}: Step Name is required`);
        } else if (field === 'service_time') {
            return stepError.includes(`Step ${stepNumber}: Service Time is required`) ||
                stepError.includes(`Step ${stepNumber}: Service Time must be a valid number greater than 0`);
        }

        return false;
    };

    // Function to get field-specific error message
    const getFieldErrorMessage = (stepId, field) => {
        if (!hasFieldError(stepId, field)) return '';

        const step = steps.find(s => s.id === stepId);
        if (!step) return '';

        const stepNumber = step.step;

        if (field === 'stepName') {
            return 'Step Name is required';
        } else if (field === 'service_time') {
            if (stepError.includes(`Step ${stepNumber}: Service Time must be a valid number greater than 0`)) {
                return 'Service Time must be a valid number greater than 0';
            } else {
                return 'Service Time is required';
            }
        }

        return '';
    };

    return (
        <Box className="bg-white p-8 border rounded-xl mt-5">
            <div className="text-lg font-medium my-5 flex justify-between items-center">
                <span>Service Steps</span>
            </div>
            {steps.length > 0 && (
                <div className="space-y-6">
                    {steps.map((step) => (
                        <div key={step.id} className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="font-medium text-gray-700 text-lg">Step {step.step}</h4>
                                {steps.length > 1 && (
                                    <button
                                        onClick={() => removeStep(step.id)}
                                        className="text-red-500 hover:text-red-700 p-1 rounded"
                                        title="Remove Step"
                                        disabled={isLoading}
                                    >
                                        <Trash size={18} />
                                    </button>
                                )}
                            </div>

                            {/* Step Name & Time */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Step Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={step.stepName}
                                        onChange={(e) => updateStep(step.id, 'stepName', e.target.value)}
                                        placeholder="Enter step name"
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${hasFieldError(step.id, 'stepName') ? 'border-red-500' : 'border-gray-300'}`}
                                        disabled={isLoading}
                                    />
                                    {hasFieldError(step.id, 'stepName') && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {getFieldErrorMessage(step.id, 'stepName')}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Service Time (minutes) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={step.service_time}
                                        onChange={(e) => updateStep(step.id, 'service_time', e.target.value)}
                                        onWheel={handleWheel}
                                        placeholder="Enter time in minutes (e.g., 10, 20, 30)"
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${hasFieldError(step.id, 'service_time') ? 'border-red-500' : 'border-gray-300'}`}
                                        disabled={isLoading}
                                    />
                                    {hasFieldError(step.id, 'service_time') && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {getFieldErrorMessage(step.id, 'service_time')}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        Enter time in minutes only (e.g., 10, 20, 30, 45, 60)
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Step Button */}
            <div className="text-lg font-medium mt-5 flex justify-end items-center">
                <AppButton
                    buttontext="Add Step"
                    onClick={addStep}
                    disabled={isLoading}
                    icon={CirclePlus}
                />
            </div>

            {/* Empty State */}
            {steps.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <p>No steps added yet. Click "Add Step" to create your first step.</p>
                </div>
            )}
        </Box>
    );
}

export default SalonServiceSteps;
